import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PAC } from 'data/BusinessConstants';
import { createQuoteChangeOrder } from 'gql/ChangeOrder/mutations/CreateQuoteChangeOrder';
import { getQuoteChangeOrder } from 'gql/ChangeOrder/queries/GetQuoteChangeOrder';
import { ChangeOrderForm, ChangeOrderGeneralInformation, S3File } from 'interfaces/ChangeOrderForm';
import { AppDispatch, RootState } from 'store';
import { UIInput } from 'types/uiTypes';
import { convertSystemErrorToString } from 'util/errorUtils';

import { WizardProps } from '@amzn/awsui-components-react-v3';

import {
  EXPECTED_DELIVERY_DATE_PROPERTY,
  GCM_PROPERTY,
  OSD_IMPACT_COMMENT_PROPERTY,
  OSD_IMPACT_PROPERTY,
  WORK_PERFORMED_PROPERTY,
} from '../../../pages/ChangeOrder/common/ChangeOrderPropertyConstants';
import { ChangeOrderWizardStep, NEW_CHANGE_ORDER_FORM } from './constants';
import { createNewChangeOrderForm } from './helper';
import { ErrorKey, getErrorMessage, validateGeneralInformation } from './validators';

export const SLICE_NAME = 'changeOrderWizard';

/**
 * Validations errors for all Change Order steps.
 *
 * Inside this redux slice, we only save error keys because different UI will show different
 * messages. We don't want to save messages in redux store.
 */
export interface ChangeOrderValidationErrors {
  generalInformationStep: ErrorKey[];
}

const initialValidationErrorKeys: ChangeOrderValidationErrors = {
  generalInformationStep: []
};

export interface ChangeOrderWizardState {
  isNewRequest: boolean;
  activeStepIndex: number;
  wizardData: ChangeOrderForm;
  errors: {
    validationErrors: ChangeOrderValidationErrors;
    navigationError: string;
    loadingChangeOrderForEditError: string;
  };
  status: {
    // Whether Change Order wizard is loading the next step. This is triggered by wizard navigation.
    isLoadingNextStep: boolean;
    // Whether the "EditChangeOrder" page is loading. This is triggered by clicking
    // "Edit" from change order details view.
    isLoadingChangeOrderForEdit: boolean;
  };
  visibility: {
    isSubmitModalVisible: boolean;
    isLockWarningModalVisible: boolean;
  };
  isGeneralInfoLocked: boolean;
  hasSeenLockWarning: boolean;
}

export interface OnChangeOrderInformationChangeParam {
  field: keyof ChangeOrderGeneralInformation;
  value: ChangeOrderForm['generalInformation'][keyof ChangeOrderForm['generalInformation']];
}

export interface SetChangeOrderWizardErrorParam {
  errorKey: keyof ChangeOrderWizardState['errors'];
  error: ChangeOrderWizardState['errors'][keyof ChangeOrderWizardState['errors']];
}

export interface SetChangeOrderWizardStepErrorParam {
  stepKey: keyof ChangeOrderValidationErrors;
  errors: ErrorKey[];
}

export interface SetChangeOrderWizardStatusParam {
  statusKey: keyof ChangeOrderWizardState['status'];
  status: ChangeOrderWizardState['status'][keyof ChangeOrderWizardState['status']];
}

export interface SetChangeOrderWizardVisibilityParam {
  visibilityKey: keyof ChangeOrderWizardState['visibility'];
  visibility: ChangeOrderWizardState['visibility'][keyof ChangeOrderWizardState['visibility']];
}

const initialState: ChangeOrderWizardState = {
  isNewRequest: true,
  activeStepIndex: ChangeOrderWizardStep.GeneralInformation,
  wizardData: NEW_CHANGE_ORDER_FORM,
  errors: {
    validationErrors: initialValidationErrorKeys,
    navigationError: '',
    loadingChangeOrderForEditError: ''
  },
  status: {
    isLoadingNextStep: false,
    isLoadingChangeOrderForEdit: false
  },
  visibility: {
    isSubmitModalVisible: false,
    isLockWarningModalVisible: false
  },
  isGeneralInfoLocked: false,
  hasSeenLockWarning: false
};

interface CreateNewChangeOrderRequestPayload {
  user: string;
  rfqResponseId?: string;
  rfqRevision?: number;
}

/**
 * Slice of Change Order wizard and details view
 */
const changeOrderWizardSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    createNewChangeOrderRequest(state, action: PayloadAction<CreateNewChangeOrderRequestPayload>) {
      const { user, rfqResponseId, rfqRevision } = action.payload;
      const newForm = createNewChangeOrderForm({ user });
      
      return {
        ...initialState,
        wizardData: {
          ...newForm,
          // Automatically set to RFQ type when coming from RFQ context
          changeOrderType: rfqResponseId ? 'RFQ' as any : newForm.changeOrderType,
          // Explicitly set RFQ fields - only use provided values if they exist
          // Otherwise ensure they are completely cleared
          rfqResponseId: rfqResponseId || '',
          rfqRevision: rfqRevision || 0
        }
      };
    },
    setChangeOrderActiveStepIndex(state, action: PayloadAction<number>) {
      state.activeStepIndex = action.payload;
    },
    setChangeOrderIsNew(state, action: PayloadAction<boolean>) {
      state.isNewRequest = action.payload;
    },
    setChangeOrderWizardData(state, action: PayloadAction<ChangeOrderForm>) {
      return {
        ...state,
        wizardData: action.payload
      };
    },
    setChangeOrderWizardError(state, action: PayloadAction<SetChangeOrderWizardErrorParam>) {
      const { errorKey, error } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          [errorKey]: error
        }
      };
    },
    // Set validation error for one step
    setChangeOrderWizardStepError(state, action: PayloadAction<SetChangeOrderWizardStepErrorParam>) {
      const { stepKey, errors } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: {
            ...state.errors.validationErrors,
            [stepKey]: errors
          }
        }
      };
    },
    // Clear validation error for one step
    clearChangeOrderWizardStepError(state, action: PayloadAction<{ stepKey: keyof ChangeOrderValidationErrors }>) {
      const { stepKey } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: {
            ...state.errors.validationErrors,
            [stepKey]: []
          }
        }
      };
    },
    // Set Change Order loading status
    setChangeOrderWizardStatus(state, action: PayloadAction<SetChangeOrderWizardStatusParam>) {
      const { statusKey, status } = action.payload;
      return {
        ...state,
        status: {
          ...state.status,
          [statusKey]: status
        }
      };
    },
    // Set visibility of the Change Order modals
    setChangeOrderWizardVisibility(state, action: PayloadAction<SetChangeOrderWizardVisibilityParam>) {
      const { visibilityKey, visibility } = action.payload;
      return {
        ...state,
        visibility: {
          ...state.visibility,
          [visibilityKey]: visibility
        }
      };
    },
    onChangeOrderInformationChange(state, action: PayloadAction<OnChangeOrderInformationChangeParam>) {
      const { field, value } = action.payload;
      state.wizardData.generalInformation = {
        ...state.wizardData.generalInformation,
        [field]: value
      };
    },
    updateInputFiles(state, action: PayloadAction<S3File[]>) {
      if (!state.wizardData.documents) {
        state.wizardData.documents = { inputFiles: [] };
      }
      state.wizardData.documents.inputFiles = action.payload;
    },
    updateAdditionalFiles(state, action: PayloadAction<S3File[]>) {
      if (!state.wizardData.documents) {
        state.wizardData.documents = { inputFiles: [], additionalFiles: [] };
      }
      state.wizardData.documents.additionalFiles = action.payload;
    },
    setChangeOrderId: (state, action: PayloadAction<string>) => {
      state.wizardData.changeOrderId = action.payload;
    },
    setGeneralInfoLocked(state, action: PayloadAction<boolean>) {
      state.isGeneralInfoLocked = action.payload;
    },
    setChangeOrderType(state, action: PayloadAction<string>) {
      state.wizardData.changeOrderType = action.payload as any;
    },
    setBusinessType(state, action: PayloadAction<string>) {
      state.wizardData.businessId = action.payload;
    },
    setHasSeenLockWarning(state, action: PayloadAction<boolean>) {
      state.hasSeenLockWarning = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(onChangeOrderNavigate.pending, (state) => {
      state.status.isLoadingNextStep = true;
    });
    builder.addCase(onChangeOrderNavigate.fulfilled, (state) => {
      state.status.isLoadingNextStep = false;
    });
    builder.addCase(onChangeOrderNavigate.rejected, (state, action) => {
      state.status.isLoadingNextStep = false;
      state.errors.navigationError = convertSystemErrorToString(action.error);
    });
  }
});

const formatToISODate = (date: string | undefined): string => {
  if(date) {
    return new Date(date).toISOString().split('T')[0];
  }
  return '';
};

const determineRFQType = (type: string): string => {
  return type === 'PAC_NON_RFQ' ? 'PAC_NON_RFQ' : 'RFQ';
};

export interface OnChangeOrderNavigateUIInput extends UIInput {
  detail: WizardProps.NavigateDetail;
}

export const onChangeOrderNavigate = createAsyncThunk<
  void,
  OnChangeOrderNavigateUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onChangeOrderNavigate`, async (inputArgs, thunkApi) => {
  const { getState, dispatch } = thunkApi;
  const { activeStepIndex, wizardData, isGeneralInfoLocked, hasSeenLockWarning } = getState()[SLICE_NAME];
  const { apolloClient, detail } = inputArgs;

  // If already locked, maintain the lock state
  if (isGeneralInfoLocked) {
    dispatch(setChangeOrderActiveStepIndex(detail.requestedStepIndex));
    return;
  }

  // For General Information step
  if (activeStepIndex === ChangeOrderWizardStep.GeneralInformation) {
    const errors = validateGeneralInformation(wizardData.generalInformation, wizardData.changeOrderType);
    if (errors.length > 0) {
      dispatch(setChangeOrderWizardStepError({ 
        stepKey: 'generalInformationStep', 
        errors 
      }));
      return;
    }

    // Show warning modal if user hasn't seen it yet
    if (!hasSeenLockWarning) {
      dispatch(setChangeOrderWizardVisibility({ 
        visibilityKey: 'isLockWarningModalVisible', 
        visibility: true 
      }));
      return; // Stop here, wait for user confirmation
    }

      try {
        const properties = [];
        if (wizardData.businessId === PAC) {
          // Add FBNs property if applicable
          const fbns = wizardData.generalInformation.fbns?.filter(fbn => fbn && fbn.trim() !== '') || [];
          if (fbns.length > 0) {
            properties.push({
              name: "FBNS",
              value: JSON.stringify(fbns)
            });
          }
          
          // Add impact details as properties (PAC-specific)
          properties.push({
            name: WORK_PERFORMED_PROPERTY,
            value: String(wizardData.generalInformation.workPerformed)
          });
          
          properties.push({
            name: OSD_IMPACT_PROPERTY,
            value: String(wizardData.generalInformation.osdImpact)
          });
          
          if (wizardData.generalInformation.expectedDeliveryDate) {
            properties.push({
              name: EXPECTED_DELIVERY_DATE_PROPERTY,
              value: formatToISODate(wizardData.generalInformation.expectedDeliveryDate)
            });
          }
          
          if (wizardData.generalInformation.osdImpactComments) {
            properties.push({
              name: OSD_IMPACT_COMMENT_PROPERTY,
              value: wizardData.generalInformation.osdImpactComments
            });
          }

          // pass it through properties as it's a temporary solution and approver is not in createChangeOderRequest model
          if (wizardData.generalInformation.gcm) {
            properties.push({
              name: GCM_PROPERTY,
              value: wizardData.generalInformation.gcm
            });
          }
        }

        const input = {
          description: wizardData.generalInformation.description,
          businessType: wizardData.businessId,
          changeOrderType: determineRFQType(wizardData.changeOrderType),
          purchaseOrderIds: wizardData.generalInformation.poIds,
          reasonForChange: wizardData.generalInformation.reasonForChange,
          rfqResponseId: wizardData.rfqResponseId,
          rfqRevision: wizardData.rfqRevision,
          properties: properties
        };

      const changeOrderId = await createQuoteChangeOrder(apolloClient, input);

      // Store the changeOrderId in Redux state
      dispatch(setChangeOrderId(changeOrderId));

      dispatch(setGeneralInfoLocked(true));
      
      // Clear any existing errors and move to next step
      dispatch(clearChangeOrderWizardStepError({ stepKey: 'generalInformationStep' }));
      dispatch(setChangeOrderActiveStepIndex(detail.requestedStepIndex));
    } catch (error) {
      dispatch(setChangeOrderWizardStepError({ 
        stepKey: 'generalInformationStep', 
        errors: [ErrorKey.APIError] 
      }));
      return;
    }
  } else {
    // For other steps, just update the step index
    dispatch(setChangeOrderActiveStepIndex(detail.requestedStepIndex));
  }
});


/**
 * Selector to get validation errors for a specific step in the Change Order wizard
 * 
 * @param param0 - Object containing the step key to get errors for
 * @returns A selector function that returns an object with hasError and errors properties
 */
export const selectChangeOrderStepValidationErrors = ({ stepKey }: { stepKey: keyof ChangeOrderValidationErrors }) => 
  (state: RootState) => {
    const errorKeys = state.changeOrderWizard.errors.validationErrors[stepKey] || [];
    const errors = errorKeys.map(key => getErrorMessage(key));
    
    return {
      hasError: errorKeys.length > 0,
      errors
    };
  };

export const {
  setChangeOrderActiveStepIndex,
  setChangeOrderIsNew,
  setChangeOrderWizardData,
  setChangeOrderWizardError,
  setChangeOrderWizardStepError,
  clearChangeOrderWizardStepError,
  setChangeOrderWizardStatus,
  setChangeOrderWizardVisibility,
  createNewChangeOrderRequest,
  onChangeOrderInformationChange,
  updateInputFiles,
  updateAdditionalFiles,
  setChangeOrderId,
  setGeneralInfoLocked,
  setChangeOrderType,
  setBusinessType,
  setHasSeenLockWarning
} = changeOrderWizardSlice.actions;

export default changeOrderWizardSlice.reducer;
